import type { TrafficTopDimension, TrafficTopRow } from '@bartizo/shared';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { formatBytes, formatPercent } from '../lib/format.js';
import { trafficRisk } from '../lib/traffic-risk.js';
import { Facts } from '../ui/DataTable.js';
import type { TrafficScope } from '../api/traffic.js';
import { Flag } from './Flag.js';
import { RiskBadge } from './StatusBadge.js';
import { blockablePath } from '../lib/block-page.js';
import { hasRelated } from '../lib/traffic-related.js';
import { TrafficRelated } from './TrafficRelated.js';

/**
 * Under a top-list row: its answers split out, the load it put on the server and
 * the reading of its risk. Every figure comes from the row's own counts; rule 19
 * leaves nothing finer (no per-request or cross-list joins), so what is not here
 * is not kept.
 */

const count = (value: number): string => Math.round(value).toLocaleString('en-US');
const share = (part: number, whole: number): string =>
  whole === 0 ? '—' : formatPercent((part / whole) * 100, 0);

export function TrafficRowDetail({
  dimension,
  row,
  scope,
  addressAction,
  blockPage,
}: {
  dimension: TrafficTopDimension;
  row: TrafficTopRow;
  /** With it, the panel also shows what is joined to the row (P11.13). */
  scope?: TrafficScope | undefined;
  /** A button beside each address in those lists: the ban, where this server can enforce one. */
  addressAction?: ((ip: string) => ReactNode) | undefined;
  /** For a page row: where "block this page" goes; absent for a person who cannot make rules. */
  blockPage?: ((path: string) => string) | undefined;
}): ReactNode {
  const ok = Math.max(0, row.requests - row.status4xx - row.status5xx);
  const reading = trafficRisk(row);
  const isAddress = dimension === 'ip' || dimension === 'site_ip';
  return (
    <div className="detail-grid">
      <section>
        <h4>Answers</h4>
        <Facts
          items={[
            ['Requests', count(row.requests)],
            ['Served (2xx and 3xx)', `${count(ok)} · ${share(ok, row.requests)}`],
            ['Refused (4xx)', `${count(row.status4xx)} · ${share(row.status4xx, row.requests)}`],
            ['Failed (5xx)', `${count(row.status5xx)} · ${share(row.status5xx, row.requests)}`],
          ]}
        />
      </section>
      <section>
        <h4>Load</h4>
        <Facts
          items={[
            ['Data sent', formatBytes(row.bytes)],
            ['Average response', `${count(row.avgResponseMs)} ms`],
            [
              'Per request',
              row.requests === 0 ? '—' : formatBytes(Math.round(row.bytes / row.requests)),
            ],
          ]}
        />
      </section>
      <section>
        <h4>Reading</h4>
        <Facts
          items={[
            ['Risk', <RiskBadge key="r" risk={reading.risk} why={reading.why} />],
            ['Why', reading.why],
            ...(row.botCategory === undefined
              ? []
              : ([['Visitor type', row.botCategory.replace(/_/g, ' ')]] as [string, ReactNode][])),
            ...(row.verification === undefined
              ? []
              : ([['Bot check', row.verification]] as [string, ReactNode][])),
          ]}
        />
      </section>
      {isAddress && row.geo !== undefined && (
        <section>
          <h4>Where</h4>
          <Facts
            items={[
              ['Country', <Flag key="c" code={row.geo.country} name />],
              ['Network', row.geo.network ?? '—'],
              ['AS number', row.geo.asn === null ? '—' : `AS${String(row.geo.asn)}`],
            ]}
          />
        </section>
      )}
      {scope !== undefined && hasRelated(dimension) && (
        <TrafficRelated
          dimension={dimension}
          keyValue={row.key}
          scope={scope}
          addressAction={addressAction}
        />
      )}
      {isAddress && (
        <section>
          <h4>Go to</h4>
          <Link to={`/addresses/${encodeURIComponent(row.key)}`}>Open the address</Link>
        </section>
      )}
      {dimension === 'path' && blockPage !== undefined && (
        <BlockPage path={row.key} link={blockPage} />
      )}
    </div>
  );
}

/** "Block this page": the rule, started in watch mode, or why this page cannot be one. */
function BlockPage({ path, link }: { path: string; link: (path: string) => string }): ReactNode {
  const verdict = blockablePath(path);
  return (
    <section>
      <h4>Block it</h4>
      {verdict.ok ? (
        <>
          <Link className="button button-secondary" to={link(path)}>
            Block this page…
          </Link>
          <p className="muted text-xs">
            Starts in watch mode: it counts the requests it would refuse, for you to check before it
            refuses any.
          </p>
        </>
      ) : (
        <p className="muted text-sm">{verdict.reason}</p>
      )}
    </section>
  );
}
